import { Component, computed, inject, input, output, signal } from '@angular/core'
import {
  booleanEntityDetailsFields,
  dateEntityDetailsFields,
  Entity,
  EntityDetails,
  EntityDetailsProperties,
  EntityDetailsField,
  entityDetailValueLists,
  EntityTypeCode,
  EntityTypeMetadata,
  getDetailsFieldLabel,
  numberEntityDetailsFields,
  stringEntityDetailsFields,
  ValidationMessage,
} from './entity-metadata'
import { validateNewEntity } from './validation'
import { noTerminationTerminationDate } from '../shared/utilities/date'
import { EntityManagementService } from './entity-management.service'
import { FormsModule } from '@angular/forms'
import { EntityTextFieldComponent } from './entity-text-field.component'
import { EntityDateFieldComponent } from './entity-date-field.component'
import { EntitySelectFieldComponent } from './entity-select-field.component'
import { Option } from '../shared/models/general'
import { booleanOptions } from '../shared/utilities/formatters'

export type NewEntity = Readonly<{
  entity_code?: string
  entity_name?: string
}> & Partial<EntityDetails>

type FieldType =  'required' | 'optional' | 'disabled'

type FieldInputType = 'text' | 'number' | 'boolean' | 'date'

const newEntityFields = ['entity_code', 'entity_name'] as const

type NewEntityField = typeof newEntityFields[number]

export const newEntityFieldLabels: Readonly<Record<NewEntityField, string>> = {
  entity_name: 'Name',
  entity_code: 'Code',
}

type FieldSpec = Readonly<{
  field: EntityDetailsField | NewEntityField
  inputType: FieldInputType
}>

const fieldSpecs: readonly FieldSpec[] = [
  { field: 'entity_code', inputType: 'text' },
  { field: 'entity_name', inputType: 'text' },
  ...dateEntityDetailsFields.map((field: EntityDetailsField): FieldSpec => ({ field, inputType: 'date' })),
  ...stringEntityDetailsFields.map((field: EntityDetailsField): FieldSpec => ({ field, inputType: 'text' })),
  ...numberEntityDetailsFields.map((field: EntityDetailsField): FieldSpec => ({ field, inputType: 'number' })),
  ...booleanEntityDetailsFields.map((field: EntityDetailsField): FieldSpec => ({ field, inputType: 'boolean' })),
]

type EditedValue = string | number | boolean

@Component({
  selector: 'app-new-entity',
  imports: [
    FormsModule,
    EntityTextFieldComponent,
    EntityDateFieldComponent,
    EntitySelectFieldComponent,
  ],
  template: `
    <h2>New {{ entityTypeName() }}</h2>
    @if(entityTypeMetadata()) {
      @for (spec of filteredFieldSpecs(); track spec.field) {
        @if (spec.inputType === 'date') {
          <app-entity-date-field
              [id]="spec.field"
              [label]="getLabel(spec.field)"
              [validationMessage]="getValidationMessage(spec)"
              [value]="fieldValue(spec)"
              (valueChange)="onEdit(spec, $event)"
          />
        } @else if (hasOptions(spec)) {
          <app-entity-select-field
              [id]="spec.field"
              [label]="getLabel(spec.field)"
              [options]="getOptions(spec)"
              [validationMessage]="getValidationMessage(spec)"
              [value]="fieldValue(spec)"
              (valueChange)="onEdit(spec, $event)"
          />
        } @else {
          <app-entity-text-field
              [id]="spec.field"
              [label]="getLabel(spec.field)"
              [inputType]="spec.inputType === 'number' ? 'number' : 'text'"
              [validationMessage]="getValidationMessage(spec)"
              [value]="fieldValue(spec)"
              (valueChange)="onEdit(spec, $event)"
          />
        }
      }
    }
    <div class='buttons mt-4'>
      <button
          type='button'
          class='btn me-2'
          [class.btn-outline-primary]="!saveDisabled()"
          [class.btn-outline-secondary]="saveDisabled()"
          [disabled]="saveDisabled()"
          (click)="onSave()"
      >
        Save Changes
      </button>
      <button
          type='button'
          class='btn btn-outline-secondary'
          (click)="cancel.emit()"
      >
        Cancel
      </button>
    </div>
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
      overflow: auto;
      
      h2 {
        margin-top: 0.5rem;
      }
    }
  `,
})
export class NewEntityComponent {
  private readonly entityManagementService = inject(EntityManagementService)

  readonly entities = input.required<readonly Entity[]>()
  readonly entityTypeMetadata = input.required<EntityTypeMetadata | undefined>()

  readonly saveComplete = output<string>()
  readonly cancel = output()
  readonly error = output<Error>()

  protected readonly newEntity = signal<NewEntity>({ termination_date: noTerminationTerminationDate })
  protected readonly saving = signal(false)

  protected readonly filteredFieldSpecs = computed(() =>
    fieldSpecs.filter(spec =>
      newEntityFields.includes(spec.field as NewEntityField) || this.getFieldType(spec.field as EntityDetailsField) !== 'disabled',
    ),
  )

  protected readonly entityCodes = computed<ReadonlySet<string>>(() =>
    new Set(this.entities().map(_ => _.entity_code)),
  )

  protected validationMessages = computed((): readonly ValidationMessage[] => {
    const metadata = this.entityTypeMetadata()
    return metadata ? validateNewEntity(this.newEntity(), metadata, this.entityCodes()) : []
  })

  protected valid = computed(() => this.validationMessages().length === 0)

  protected saveDisabled = computed(() => !this.valid() || this.saving())

  protected readonly entityTypeName = computed(() => {
    return this.entityTypeMetadata()?.entity_type_name ?? ''
  })

  protected getLabel = (field: string): string =>
    newEntityFieldLabels[field as NewEntityField] ?? getDetailsFieldLabel(field, this.entityTypeMetadata()?.entity_type_code)

  protected getFieldType (field: EntityDetailsField): FieldType | undefined {
    const entityTypeMetadata = this.entityTypeMetadata()
    if (entityTypeMetadata) {
      const { required, optional } = entityTypeMetadata
      if (required.includes(field)) {
        return 'required'
      } else if (optional.includes(field) && field !== 'termination_date') { // don't show end date on new entity
        return 'optional'
      } else {
        return 'disabled'
      }
    }
    return undefined
  }

  protected entityTypeCode(): EntityTypeCode {
    if (this.entityTypeMetadata()) {
      return this.entityTypeMetadata()!.entity_type_code
    } else {
      throw new Error('entity metadata missing')
    }
  }

  protected fieldValue<T>(spec: FieldSpec): T | undefined {
    return this.newEntity()[spec.field] as T | undefined
  }

  protected hasOptions(spec: FieldSpec): boolean {
    return Boolean(entityDetailValueLists[this.entityTypeCode()][spec.field as EntityDetailsField]) || spec.inputType === 'boolean'
  }

  protected getOptions(spec: FieldSpec): readonly Option<string | boolean>[] {
    if (spec.inputType === 'boolean') {
      return [booleanOptions.true, booleanOptions.false]
    } else {
      const valueList = entityDetailValueLists[this.entityTypeCode()][spec.field as EntityDetailsField]
      return valueList ? valueList.map(value => ({ display: value, value })) : []
    }
  }

  protected getValidationMessage(spec: FieldSpec): string | undefined {
    return this.validationMessages().find(message =>
      message.targets.some(_ => _.id === spec.field),
    )?.text
  }

  protected onEdit({ field }: FieldSpec, value: EditedValue | undefined) {
    const newEntity: Record<string, unknown> = { ...this.newEntity() }

    const coerced = typeof value === 'string' ? (value.trim() || undefined) : value
    if (coerced == null) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newEntity[field]
    } else {
      if (field === 'entity_code' && newEntity['entity_code'] === newEntity['entity_name']) {
        newEntity['entity_name'] = coerced
      }
      newEntity[field] = coerced
    }
    this.newEntity.set(newEntity as NewEntity)
  }

  protected onSave() {
    const entityTypeCode = this.entityTypeCode()
    this.saving.set(true)
    const { entity_code, entity_name, ...rest } = this.newEntity()
    const entityDetails = this.entityTypeMetadata()?.editable ? rest as EntityDetailsProperties: undefined
    this.entityManagementService.createNewEntity(entityTypeCode, entity_code!, entity_name!, entityDetails)
      .subscribe({
        next: (entityUid: string) => {
          this.saving.set(false)
          this.saveComplete.emit(entityUid)
        },
        error: error => {
          this.saving.set(false)
          this.error.emit(error as Error)
        },
      })
  }
}
